/**
 * Read-only view of a unified diff produced by the server (`unified_diff` in the editor MCP
 * content rules): added lines in green, removed lines in red, hunk headers muted, file headers
 * hidden. It only colours lines by their first character; the diff itself is computed in Rust.
 *
 * @module apps/web/src/components/flux-agent/FluxAgentDiffView
 */

import { cn } from '@/lib/utils';

/** Inputs for {@link FluxAgentDiffView}. */
export interface FluxAgentDiffViewProps {
  /** Unified diff text with `---`/`+++` file headers and `@@` hunks; empty when nothing changed. */
  diff: string;
  /** Accessible label for the diff region, such as the file path. */
  label: string;
}

/** Render a unified diff as coloured, horizontally scrollable lines. */
export function FluxAgentDiffView({ diff, label }: FluxAgentDiffViewProps) {
  const lines = diff
    .split('\n')
    .filter(line => line && !line.startsWith('--- ') && !line.startsWith('+++ '));
  if (!lines.length) {
    return <p className="text-muted-foreground text-xs">No changes to the text.</p>;
  }
  return (
    <pre
      className="bg-background max-h-80 overflow-auto rounded-md border text-xs leading-5"
      aria-label={label}
    >
      <code className="block min-w-max">
        {lines.map((line, index) => {
          const kind = line.startsWith('@@')
            ? 'hunk'
            : line.startsWith('+')
              ? 'add'
              : line.startsWith('-')
                ? 'remove'
                : 'context';
          return (
            <span
              key={index}
              data-diff={kind}
              className={cn(
                'block px-2',
                kind === 'add' && 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-300',
                kind === 'remove' && 'bg-red-500/15 text-red-800 dark:text-red-300',
                kind === 'hunk' && 'text-muted-foreground bg-muted/60',
                kind === 'context' && 'text-foreground/80'
              )}
            >
              {kind === 'add' && <span className="sr-only">Added: </span>}
              {kind === 'remove' && <span className="sr-only">Removed: </span>}
              {line}
            </span>
          );
        })}
      </code>
    </pre>
  );
}
